/**
 * ProviderLayout — single layout for both payment_agent and ticket_provider.
 * Menu items are filtered by role. Includes notification bell + WS status.
 */
import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import {
  LayoutDashboard, Inbox, FileText, Settings, LogOut, Bell, Truck, Ticket,
  CreditCard, ChevronRight, Eye, Menu, X, Activity, ArrowLeftRight, Wallet,
  Globe, Pause,
} from 'lucide-react';
import NotificationToggle from './NotificationToggle';
import EnablePushBanner from './EnablePushBanner';
import { listNotifications, markRead, markAllRead } from '@/services/realtimeApi';
import ConnectivityBanner, { OnlinePill } from '@/components/ConnectivityBanner';
import { clearCache } from '@/services/resilientCache';

const ProviderLayout = () => {
  const { user, logout, isProvider, getAuthHeaders, loading, isPreviewSession } = useAuth();
  const { status, subscribe } = useRealtime();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [showBell, setShowBell] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const previewAdminName = (() => {
    if (!isPreviewSession) return null;
    try {
      const t = window.sessionStorage.getItem('preview_token');
      if (!t) return null;
      const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return p.preview_admin_name || null;
    } catch { return null; }
  })();
  const previewMode = (() => {
    if (!isPreviewSession) return null;
    try {
      const t = window.sessionStorage.getItem('preview_token');
      if (!t) return null;
      const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return (p.preview_mode || 'readonly').toLowerCase();
    } catch { return 'readonly'; }
  })();

  const refreshNotifs = async () => {
    try {
      const r = await listNotifications(getAuthHeaders, { limit: 20 });
      setNotifs(r.notifications || []);
      setUnread(r.unread || 0);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ProviderLayout] notifications refresh failed', err);
      }
    }
  };

  useEffect(() => {
    if (!isProvider()) return;
    refreshNotifs();
    const off = subscribe('notification.new', () => { refreshNotifs(); });
    return () => off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner w-10 h-10" /></div>;
  if (!user || !isProvider()) return <Navigate to="/provider/login" replace />;

  const isPaymentAgent = user.role === 'payment_agent';

  // Payment agents (proveedor de monedas) NO venden tickets ni reciben
  // pagos de clientes — solo entregan remesas. Por eso ocultamos
  // "Métodos de Pago" (que está pensado para que un ticket_provider o un
  // gestor declare sus cuentas de cobro) y dejamos solo lo que realmente
  // usan: Panel, Mis Pagos, Cola Operacional, Documentos, Mi cuenta.
  const nav = [
    { path: '/provider', icon: Globe, label: 'Inicio' },
    { path: '/provider/dashboard', icon: LayoutDashboard, label: 'Panel' },
    { path: '/provider/operaciones', icon: isPaymentAgent ? Truck : Ticket, label: isPaymentAgent ? 'Mis Pagos' : 'Mis Tickets' },
    ...(isPaymentAgent ? [
      { path: '/provider/remesas/historial-detallado', icon: Activity,      label: 'Historial Detallado' },
      { path: '/provider/zona-de-cambios',             icon: ArrowLeftRight, label: '💱 Zona de Cambios' },
      { path: '/provider/recargas-usdt',               icon: Wallet,         label: 'Recargas USDT' },
      { path: '/provider/card-bins',                   icon: CreditCard,     label: 'Registro de BINs' },
      { path: '/provider/pausa',                       icon: Pause,          label: 'Pausa de Servicios' },
    ] : []),
    { path: '/provider/queues', icon: Inbox, label: 'Cola Operacional' },
    ...(isPaymentAgent ? [{ path: '/provider/wallet', icon: CreditCard, label: 'Mi Wallet (50%)' }] : []),
    ...(isPaymentAgent ? [{ path: '/provider/datos-cobro', icon: Wallet, label: '💰 Datos de cobro' }] : []),
    ...(isPaymentAgent ? [] : [{ path: '/provider/metodos', icon: CreditCard, label: 'Métodos de Pago' }]),
    { path: '/provider/documentos', icon: FileText, label: 'Documentos' },
    { path: '/provider/cuenta', icon: Settings, label: 'Mi cuenta' },
  ];

  const onLogout = () => { clearCache(user?.id); logout(); navigate('/provider/login'); };
  const onClickNotif = async (n) => {
    if (!n.read) {
      try {
        await markRead(getAuthHeaders, n.id);
        refreshNotifs();
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[ProviderLayout] markRead failed', err);
        }
      }
    }
    if (n.operation_id) navigate(`/provider/operaciones?op=${n.operation_id}`);
    setShowBell(false);
  };
  const onMarkAll = async () => { await markAllRead(getAuthHeaders); refreshNotifs(); };

  return (
    <>
      {isPreviewSession && (
        <div
          className={`text-white text-xs sm:text-sm flex items-center justify-center gap-2 py-2 px-3 sticky top-0 z-50 ${
            previewMode === 'simulation' ? 'bg-amber-600' : 'bg-rose-600'
          }`}
          data-testid="preview-banner"
        >
          <Eye className="w-4 h-4" />
          <span>
            {previewMode === 'simulation' ? (
              <>
                <b>🧪 Modo Simulación</b> · Tus acciones se persisten con <b>is_simulation=true</b> y NO afectan reportes reales. Iniciado por <b>{previewAdminName || 'admin'}</b>.
              </>
            ) : (
              <>
                <b>Vista previa</b> · Estás viendo el portal como este proveedor (solo lectura). Sesión iniciada por <b>{previewAdminName || 'admin'}</b>.
              </>
            )}
          </span>
        </div>
      )}
      <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setMobileOpen(false)} data-testid="provider-sidebar-backdrop" />
      )}
      <aside
        className={`w-64 bg-[#0f1525] text-slate-200 flex flex-col fixed lg:static inset-y-0 left-0 z-40 transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="provider-sidebar"
      >
        <div className="p-5 border-b border-white/10 flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white text-sm font-bold">Portal Proveedor</h1>
            <p className="text-[10px] text-slate-400">{isPaymentAgent ? 'Agente de Pago' : 'Proveedor de Tickets'}</p>
          </div>
          {/* Close-X on mobile */}
          <button
            className="lg:hidden text-slate-400 hover:text-white p-1"
            onClick={() => setMobileOpen(false)}
            data-testid="provider-sidebar-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map(({ path, icon: Ic, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/provider'}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-white/5'}`}
              data-testid={`provider-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Ic className="w-4 h-4" />
              <span>{label}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-30" />
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-2 text-[10px] text-slate-400">
            <OnlinePill />
          </div>
          <NotificationToggle />
        </div>
        <button onClick={onLogout} className="m-3 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-2 text-rose-300" data-testid="provider-logout">
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </aside>

      <main className="flex-1 min-w-0 w-full flex flex-col min-h-screen">
        <ConnectivityBanner />
        <header className="bg-white border-b px-3 sm:px-5 py-3 flex items-center justify-between gap-2">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded hover:bg-slate-100"
            onClick={() => setMobileOpen(true)}
            data-testid="provider-mobile-menu"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="text-xs text-slate-500 flex-1 min-w-0 truncate">Hola, <span className="font-semibold text-slate-900">{user.name}</span></div>
          <div className="relative">
            <button onClick={() => setShowBell(!showBell)} className="relative p-2 rounded hover:bg-slate-100" data-testid="provider-bell">
              <Bell className="w-5 h-5 text-slate-700" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 text-white text-[9px] rounded-full flex items-center justify-center" data-testid="provider-bell-count">
                  {unread}
                </span>
              )}
            </button>
            {showBell && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-2xl border z-50" data-testid="provider-bell-panel">
                <div className="p-3 border-b flex items-center justify-between">
                  <p className="text-sm font-bold">Notificaciones</p>
                  {unread > 0 && <button onClick={onMarkAll} className="text-[10px] text-blue-600">Marcar todas leídas</button>}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <p className="text-xs text-slate-500 italic text-center py-6">Sin notificaciones</p>
                  ) : notifs.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => onClickNotif(n)}
                      data-testid={`notif-${n.id}`}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-0 ${!n.read ? 'bg-blue-50' : ''}`}
                    >
                      <p className="text-xs font-semibold text-slate-900">{n.title}</p>
                      <p className="text-[10px] text-slate-500">{n.body}</p>
                      <p className="text-[9px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <EnablePushBanner audience="provider" />
        <div className="flex-1 p-3 sm:p-4 lg:p-6 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
    </>
  );
};

export default ProviderLayout;
